import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, TrendingDown, Zap, Activity, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ASSETS } from "@/data/assets";

const HERO = ASSETS.running;
const GOLF = ASSETS.golfing;

const SQUEEZE_POINTS = [
  "Quotas keep climbing while your selling time gets crushed by internal meetings, clunky systems, and compliance work.",
  "New competitors appear daily. The deals you chase are tougher.",
  "Many \u201CAI solutions\u201D feel like expensive toys instead of real leverage.",
];

export default function CorporateAthlete() {
  return (
    <div data-testid="page-corporate-athlete">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0">
          <img
            src={HERO}
            alt="Trail running — high performance in motion"
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover"
            style={{ objectPosition: "50% 18%", filter: "brightness(1.05)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/85 to-background" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-36">
          <p className="font-mono-ui text-[11px] uppercase tracking-[0.28em] text-ai">
            // Framework
          </p>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.98] max-w-4xl">
            Corporate Athlete <span className="text-accent">2.0</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            I spent over 20 years in industrial sales and leadership, including
            building and scaling an IoT‑based startup that we successfully
            exited. I know what it feels like to carry a bag, hit (and miss)
            quotas, and perform under constant pressure while the rules of the
            game keep changing around you.
          </p>
          <p className="mt-4 text-lg text-foreground max-w-3xl leading-relaxed">
            This is{" "}
            <span className="font-display font-bold">Corporate Athlete 2.0</span>{" "}
            — the evolved version of the high performer who refuses to be left
            behind in the AI and agentic economy.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/insights">
              <Button
                data-testid="ca-read-insights-button"
                size="lg"
                className="rounded-sm gap-2 group"
              >
                Read the essays
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                data-testid="ca-work-with-me-button"
                size="lg"
                variant="outline"
                className="rounded-sm gap-2"
              >
                Work with me
                <Sparkles className="h-4 w-4 text-ai" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* THE SQUEEZE IS REAL */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-accent">
              // 001 — Reality
            </p>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight leading-tight">
              The Squeeze Is Real
            </h2>
            <div className="mt-6 flex items-center gap-3">
              <TrendingDown className="h-5 w-5 text-accent" />
              <p className="font-mono-ui text-xs uppercase tracking-widest text-muted-foreground">
                Working harder. Selling less.
              </p>
            </div>
          </div>
          <div className="lg:col-span-8 space-y-6">
            <p className="text-lg text-foreground/90 leading-relaxed">
              You're working harder than ever, but you're actually selling
              less.
            </p>
            <ul className="space-y-4">
              {SQUEEZE_POINTS.map((p, i) => (
                <li
                  key={i}
                  className="flex gap-4 border-l border-border pl-5 py-1 text-muted-foreground leading-relaxed"
                >
                  <span className="font-mono-ui text-[10px] uppercase tracking-widest text-accent pt-1 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <p className="text-foreground font-medium">
              This is the squeeze. And if you're feeling it, you're not alone.
            </p>
          </div>
        </div>
      </section>

      {/* THE EVOLUTION */}
      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-ai">
              // 002 — Evolution
            </p>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight leading-tight">
              The Evolution
            </h2>
            <div className="mt-6 flex items-center gap-3">
              <Zap className="h-5 w-5 text-ai" />
              <p className="font-mono-ui text-xs uppercase tracking-widest text-muted-foreground">
                From utility player to specialist
              </p>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-5">
            <p className="text-muted-foreground leading-relaxed">
              Just like elite athletes adapt when the game changes, today's
              top sales professionals are evolving from utility players chasing
              the same tired opportunities to{" "}
              <span className="text-foreground font-medium">
                Corporate Athletes 2.0
              </span>{" "}
              — specialists who use AI as a daily collaborator to find and
              create better opportunities, reclaim their time, and focus on
              what actually drives revenue: high‑value conversations with
              decision‑makers.
            </p>
            <p className="text-foreground font-medium leading-relaxed">
              This isn't about working harder. It's about working smarter in a
              landscape that rewards those who adapt fastest.
            </p>
          </div>
        </div>
      </section>

      {/* THE 1% RULE */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-accent">
              // 003 — Discipline
            </p>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight leading-tight">
              The 1% Rule
            </h2>
            <div className="mt-6 flex items-center gap-3">
              <Activity className="h-5 w-5 text-accent" />
              <p className="font-mono-ui text-xs uppercase tracking-widest text-muted-foreground">
                Compounding daily
              </p>
            </div>
          </div>
          <div className="lg:col-span-8 space-y-5">
            <p className="text-muted-foreground leading-relaxed">
              Inspired by elite athletes and masters like Thomas Keller, the
              mindset is simple:
            </p>
            <blockquote className="border-l-2 border-accent pl-6 py-2">
              <p className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-tight">
                Get <span className="text-accent">1% better</span> every single
                day. Small, consistent improvements compound into massive
                advantages over time.
              </p>
            </blockquote>
            <p className="text-muted-foreground leading-relaxed">
              This is how Corporate Athletes stay ahead — not through grand
              overhauls, but through disciplined daily practice with AI as
              their training partner.
            </p>
          </div>
        </div>
      </section>

      {/* MY PROMISE + BALANCE IMAGE */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-accent">
              // 004 — Commitment
            </p>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight leading-tight">
              My Promise
            </h2>
            <div className="mt-6 flex items-center gap-3">
              <Handshake className="h-5 w-5 text-accent" />
              <p className="font-mono-ui text-xs uppercase tracking-widest text-muted-foreground">
                No hype. Lived experience.
              </p>
            </div>
            <div className="mt-6 space-y-5 max-w-2xl">
              <p className="text-muted-foreground leading-relaxed">
                I'm not here to sell hype. I'm a 20+ year sales leader who has
                lived the squeeze. I'm using these same principles right now —
                with AI as my collaborator, editor, and sometimes my toughest
                critic.
              </p>
              <p className="text-foreground leading-relaxed">
                If you're tired of feeling like you're falling behind while
                working harder,{" "}
                <span className="font-display font-bold">
                  Corporate Athlete 2.0
                </span>{" "}
                is for you.
              </p>
              <p className="font-display font-black text-2xl tracking-tight text-accent">
                It's time to evolve.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/contact">
                <Button
                  data-testid="ca-promise-contact-button"
                  size="lg"
                  className="rounded-sm gap-2 group"
                >
                  Start a conversation
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative aspect-[4/5] w-full max-w-md ml-auto overflow-hidden border border-border">
              <img
                src={GOLF}
                alt="Vincent on the golf course — long-game thinking in practice"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
                style={{ objectPosition: "50% 20%" }}
              />
              <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-background/90 to-transparent">
                <p className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                  Off the field
                </p>
                <p className="text-sm font-medium mt-1">Long‑game rituals</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPOUNDING QUOTE */}
      <section className="mx-auto max-w-4xl px-6 lg:px-10 py-24 text-center">
        <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
          // Compounding
        </p>
        <blockquote className="mt-4 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
          "If you get 1% better every day for a year, you end up{" "}
          <span className="text-accent">37×</span> better."
        </blockquote>
        <p className="mt-6 text-sm text-muted-foreground">
          The rule is old. The application to AI‑era leadership is new.
        </p>
      </section>
    </div>
  );
}
